export type CompanionAvailabilityWindowKind =
  | "day-window"
  | "after-work"
  | "travel-time"
  | "free-text";

export type CompanionAvailabilityWindow = {
  id: string | null;
  label: string | null;
  kind: CompanionAvailabilityWindowKind | null;
  dayLabel: string | null;
  timeOfDay: "morning" | "afternoon" | "evening" | null;
  durationMinutes: number | null;
  travelMinutes: number | null;
  sourceText: string | null;
  active: boolean;
  createdAt: string | null;
  updatedAt: string | null;
};

type BuildFitLineOptions = {
  windows: CompanionAvailabilityWindow[];
  bestWindow: string | null;
  now: Date;
  timeZone: string;
  earliestStartAt?: Date;
};

/** Historical prose remains a hint until its exact intervals are reviewed. */
export function buildCompanionAvailabilityFitLine(options: BuildFitLineOptions): string | null {
  if (!options.bestWindow) return null;
  let weekday: string;
  try { weekday = new Intl.DateTimeFormat("en-GB", { timeZone: options.timeZone, weekday: "long" }).format(options.now); }
  catch { return null; }
  const hints = options.windows.filter(window => window.active && window.label?.trim()
    && (window.kind === "after-work" || window.kind === "day-window" && (!window.dayLabel || window.dayLabel === weekday)));
  if (!hints.length) return null;
  return hints.length === 1 ? `Review exact times for ${hints[0].label} in Free time.`
    : "Review the exact times of your saved free-time hints before relying on this window.";
}

export function deriveNearbyTravelTimeCapMinutes(
  windows: CompanionAvailabilityWindow[],
): number | null {
  const travelMinutes = windows
    .filter((window) => window.active && window.kind === "travel-time")
    .map((window) => window.travelMinutes)
    .filter((minutes): minutes is number => minutes !== null && minutes > 0);

  return travelMinutes.length > 0 ? Math.min(...travelMinutes) : null;
}
